import {
  add,
  addDays,
  addYears,
  differenceInMilliseconds,
  format,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  parse,
  parseISO,
  sub,
} from "date-fns";

const formatTokens = (value) =>
  value
    .replace(/\bYYYY\b/g, "yyyy")
    .replace(/\bYY\b/g, "yy")
    .replace(/\bDD\b/g, "dd")
    .replace(/\bD\b/g, "d")
    .replace(/\bddd\b/g, "EEE")
    .replace(/\bdddd\b/g, "EEEE")
    .replace(/\bA\b/g, "aa")
    .replace(/\bll\b/g, "MMM d, yyyy")
    .replace(/\bLL\b/g, "MMMM d, yyyy");

const parseInput = (value, inputFormat) => {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  if (typeof value === "number") {
    return new Date(value);
  }

  if (!value) {
    return new Date(NaN);
  }

  if (inputFormat) {
    const parsed = parse(value, formatTokens(inputFormat), new Date());
    return isValid(parsed) ? parsed : new Date(NaN);
  }

  const parsed = parseISO(String(value));
  return isValid(parsed) ? parsed : new Date(value);
};

const utcDate = (value) => {
  const date = parseInput(value);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

class DateFnsMoment {
  constructor(value, inputFormat) {
    this.value = parseInput(value, inputFormat);
    this.useUtc = false;
  }

  utc() {
    this.useUtc = true;
    return this;
  }

  format(pattern) {
    const date = this.useUtc ? utcDate(this.value) : this.value;
    return format(date, formatTokens(pattern));
  }

  isValid() {
    return isValid(this.value);
  }

  isBefore(other) {
    return isBefore(this.value, new DateFnsMoment(other).value);
  }

  isAfter(other) {
    return isAfter(this.value, new DateFnsMoment(other).value);
  }

  isSameOrBefore(other) {
    return isBefore(this.value, new DateFnsMoment(other).value) ||
      isEqual(this.value, new DateFnsMoment(other).value);
  }

  isSameOrAfter(other) {
    return isAfter(this.value, new DateFnsMoment(other).value) ||
      isEqual(this.value, new DateFnsMoment(other).value);
  }

  isBetween(start, end, _unit, inclusivity = "()") {
    const afterStart = inclusivity[0] === "["
      ? this.isSameOrAfter(start)
      : this.isAfter(start);
    const beforeEnd = inclusivity[1] === "]"
      ? this.isSameOrBefore(end)
      : this.isBefore(end);
    return afterStart && beforeEnd;
  }

  add(amount, unit) {
    this.value = unit === "year" || unit === "years"
      ? addYears(this.value, amount)
      : unit === "day" || unit === "days"
        ? addDays(this.value, amount)
        : add(this.value, { [unit]: amount });
    return this;
  }

  subtract(amount, unit) {
    this.value = sub(this.value, {
      [unit === "year" ? "years" : unit === "day" ? "days" : unit]: amount,
    });
    return this;
  }

  diff(other) {
    return differenceInMilliseconds(this.value, new DateFnsMoment(other).value);
  }

  valueOf() {
    return this.value.getTime();
  }

  date(value) {
    if (value === undefined) {
      return this.value.getDate();
    }
    this.value.setDate(value);
    return this;
  }

  clone() {
    const clone = new DateFnsMoment(this.value);
    clone.useUtc = this.useUtc;
    return clone;
  }

  toDate() {
    return new Date(this.value.getTime());
  }
}

export function dateFnsMoment(value, inputFormat, strict) {
  const result = new DateFnsMoment(value, inputFormat);
  if (strict && inputFormat && !result.isValid()) {
    result.value = new Date(NaN);
  }
  return result;
}

dateFnsMoment.utc = (value, inputFormat) => {
  const result = new DateFnsMoment(value, inputFormat);
  result.useUtc = true;
  return result;
};

dateFnsMoment.max = (...values) =>
  values.reduce((latest, value) =>
    latest.valueOf() >= value.valueOf() ? latest : value,
  );
